/**
 * Per-tab session identity guard.
 *
 * The Hub's auth tokens are host-wide cookies shared by every tab, while the
 * user a tab *displays* is per-tab React state (useUserStore). Logging into a
 * second account in the same browser therefore swaps the credentials under
 * every other tab: a tab keeps rendering user A while every API call it makes
 * is silently authenticated as user B — and a save writes to B's account
 * (exactly how notification preferences ended up on the wrong user when two
 * logins shared one browser).
 *
 * Two defenses, both driven by this module:
 *
 *  - fetchWithAuth compares the access token's `user_id` claim against the
 *    user this tab displays and refuses to send a request as anyone else,
 *    reloading the tab into the browser's real session instead.
 *  - Login/logout announce the session's user via localStorage, and every Hub
 *    tab listens (`storage` fires only in *other* tabs), so stale tabs reload
 *    immediately rather than impersonating the new session until their next
 *    API call trips the guard.
 */

/** localStorage key login/logout write so other tabs learn the session changed. */
const SESSION_USER_KEY = 'alita.session.user';

/**
 * The uuid of the user this tab is rendering. Deliberately in-memory (per-tab):
 * anything shared (cookie/localStorage) would be overwritten by the other tab's
 * login and the guard would never fire.
 */
let expectedUserUuid: string | null = null;

/** Record which user this tab is rendering (null when logged out). */
export function setExpectedSessionUser(uuid: string | null): void {
  expectedUserUuid = uuid;
}

export function getExpectedSessionUser(): string | null {
  return expectedUserUuid;
}

/**
 * The `user_id` claim (the account uuid) of a SimpleJWT token, or null when the
 * token can't be decoded. Decode-only — the backend still verifies the signature.
 */
export function tokenUserUuid(token: string): string | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims: unknown = JSON.parse(atob(base64));
    const userId = (claims as { user_id?: unknown } | null)?.user_id;
    return typeof userId === 'string' && userId ? userId : null;
  } catch {
    return null;
  }
}

/** Full-page reload: the tab re-bootstraps from whatever session the browser holds now. */
export function reloadIntoCurrentSession(): void {
  if (typeof window !== 'undefined') window.location.reload();
}

/**
 * Announce a session change (login: the new user's uuid; logout: null) to the
 * rest of the browser. The value is just the uuid — writing the same uuid twice
 * (re-login as the same account) changes nothing and fires no event.
 */
export function announceSessionUser(uuid: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (uuid) window.localStorage.setItem(SESSION_USER_KEY, uuid);
    else window.localStorage.removeItem(SESSION_USER_KEY);
  } catch {
    // Storage unavailable (private mode / quota) — the fetchWithAuth identity
    // guard still blocks wrong-user requests; tabs just won't reload eagerly.
  }
}

/**
 * Listen for session changes announced by other tabs and reload when this tab's
 * displayed user is no longer the browser's session user. Returns a cleanup fn.
 *
 * Tabs displaying nobody (login page, still bootstrapping, public agent
 * surfaces) never reload: they have no identity to contradict, and a tab mid-
 * bootstrap will hydrate the new session's user anyway.
 */
export function startSessionSync(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key !== SESSION_USER_KEY) return;
    if (!expectedUserUuid) return;
    if (event.newValue === expectedUserUuid) return;
    reloadIntoCurrentSession();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
